import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Settings2 } from "lucide-react";
import { toast } from "sonner";

export const StoreSettingsCard = () => {
  const [s, setS] = useState(null);
  const [handle, setHandle] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => { setS(r.data); setHandle(r.data.instagram_handle || ""); }).catch(() => {});
  }, []);

  const save = async (patch, msg) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", patch);
      setS(data); setHandle(data.instagram_handle || "");
      toast.success(msg);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };

  if (!s) return null;
  const dirty = handle.trim().replace(/^@/, "") !== (s.instagram_handle || "");
  return (
    <div data-testid="store-settings-card" className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-5 mb-6 space-y-4">
      <p className="font-heading font-semibold flex items-center gap-2"><Settings2 className="w-4 h-4 text-[#e2ff31]" /> Store settings</p>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm">Anonymous live purchase pop-up</p>
          <p className="text-xs text-[#6b7280]">
            {s.feed_anonymous ? "Visitors see “Someone just bought…”" : "Visitors see first name + initial, e.g. “Marcus J. just bought…”"}
          </p>
        </div>
        <Switch data-testid="feed-anonymous-switch" checked={s.feed_anonymous} disabled={saving}
          onCheckedChange={(v) => save({ feed_anonymous: v }, v ? "Pop-up is now anonymous" : "Pop-up shows buyer initials")} />
      </div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm">Print my Instagram handle on the share image</p>
          <p className="text-xs text-[#6b7280]">Adds “prod. @{s.instagram_handle || "handle"}” to the buyer's story &amp; square images.</p>
        </div>
        <Switch data-testid="card-handle-switch" checked={s.card_show_handle} disabled={saving}
          onCheckedChange={(v) => save({ card_show_handle: v }, v ? "Handle will appear on share images" : "Handle removed from share images")} />
      </div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm">24h early access for past buyers</p>
          <p className="text-xs text-[#6b7280]">New beats stay hidden from the store for 24 hours while past buyers of that genre get a private link to buy first.</p>
        </div>
        <Switch data-testid="early-access-switch" checked={s.early_access_enabled} disabled={saving}
          onCheckedChange={(v) => save({ early_access_enabled: v }, v ? "Early access is on for new drops" : "New drops go public immediately")} />
      </div>
      <div>
        <p className="text-sm">Instagram handle for buyer shares</p>
        <p className="text-xs text-[#6b7280] mb-2">Added to the Share text on the post-checkout card: “Tag @handle”.</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280] text-sm">@</span>
            <Input data-testid="instagram-handle-input" value={handle.replace(/^@/, "")} onChange={(e) => setHandle(e.target.value)}
              placeholder="yourhandle" className="pl-7 bg-[#0a0a0a] border-[#2a2a2a] focus-visible:ring-[#e2ff31]" />
          </div>
          <Button data-testid="instagram-handle-save" disabled={!dirty || saving} onClick={() => save({ instagram_handle: handle }, "Instagram handle saved")}
            className="rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};
